import { Calendar } from "lucide-react";
import React, { useEffect, useState } from "react";
import BottomSheetCalendar from "../../components/bottom-sheet-calendar";
import BackButton from "../../components/common/backButton";
import Toast from "../../components/common/toast";
import userHooks from "../../hooks/userHooks";
import useAuthStore from "../../store/authStore";

const GENDER_OPTIONS = [
  { label: "딸", value: "girl" },
  { label: "아들", value: "boy" },
  { label: "쌍둥이", value: "twins" },
  { label: "모름", value: "unknown" },
];

const convertBabyTypeForAPI = (uiType) => {
  switch (uiType) {
    case "twins":
      return "TWIN";
    case "girl":
    case "boy":
    case "unknown":
    default:
      return "SINGLE";
  }
};

const validateBirthDate = (birthDate) => {
  if (!birthDate)
    return { isValid: false, message: "출산예정일을 선택해주세요." };

  const today = new Date();
  const dueDate = new Date(birthDate);

  if (isNaN(dueDate.getTime())) {
    return { isValid: false, message: "올바른 날짜를 입력해주세요." };
  }

  // 0일(오늘) 기준으로 280일까지만 선택 가능
  const PREGNANCY_DAYS = 280;
  const minDate = new Date(today);
  minDate.setHours(0, 0, 0, 0);
  
  const maxDate = new Date(today);
  maxDate.setDate(today.getDate() + PREGNANCY_DAYS);
  maxDate.setHours(23, 59, 59, 999);

  // 선택된 날짜도 같은 기준으로 비교
  const selectedDate = new Date(dueDate);
  selectedDate.setHours(0, 0, 0, 0);

  if (selectedDate < minDate || selectedDate > maxDate) {
    const formatDate = (date) => {
      const displayDate = new Date(date);
      displayDate.setHours(0, 0, 0, 0);
      return `${displayDate.getFullYear()}년 ${displayDate.getMonth() + 1}월 ${displayDate.getDate()}일`;
    };

    const maxDisplayDate = new Date(maxDate);
    maxDisplayDate.setHours(0, 0, 0, 0);

    return {
      isValid: false,
      message: `출산예정일은 ${formatDate(minDate)}부터 ${formatDate(maxDisplayDate)} 사이여야 합니다.`,
    };
  }

  return { isValid: true, message: "" };
};

const BabyInfo = () => {  
  const userInfo = useAuthStore((state) => state.userInfo);
  const accessToken = userInfo?.accessToken;
  const updateUserInfo = useAuthStore((state) => state.updateUserInfo);

  const isEditMode = Boolean(userInfo?.baby && userInfo.baby.length > 0);

  const [name, setName] = useState(
    isEditMode ? userInfo.baby[0]?.baby_name || "" : ""
  );
  const [dueDate, setDueDate] = useState(
    isEditMode ? userInfo.baby[0]?.baby_birth || "" : ""
  );
  const [gender, setGender] = useState(
    isEditMode ? userInfo.baby[0]?.baby_type || "girl" : "girl"
  );

  const [isCalendarOpen, setIsCalendarOpen] = useState(false);
  const [isToastOpen, setIsToastOpen] = useState(false);
  const [toastMessage, setToastMessage] = useState("");
  const [isLoading, setIsLoading] = useState(false);

  const [dateValidation, setDateValidation] = useState({
    isValid: true,
    message: "",
  });
  // eslint-disable-next-line no-unused-vars
  const [showValidationError, setShowValidationError] = useState(false);

  const isFormValid = name.trim() && dueDate && dateValidation.isValid;

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  useEffect(() => {
    const validation = validateBirthDate(dueDate);
    setDateValidation(validation);

    if (dueDate && !validation.isValid) {
      setShowValidationError(true);
    } else {
      setShowValidationError(false);
    }
  }, [dueDate]);

  const handleUpdateBaby = async () => {
    if (isLoading) return;

    if (!userInfo?.baby || userInfo.baby.length === 0) {
      setToastMessage("아기 정보를 찾을 수 없습니다.");
      setIsToastOpen(true);
      return;
    }

    setIsLoading(true);

    try {
      if (!accessToken) {
        setToastMessage("로그인이 필요합니다.");
        setIsToastOpen(true);
        return;
      }

      const response = await userHooks.updateBabyHooks({
        id: userInfo.baby[0].id,
        babyName: name,
        babyBirth: dueDate,
        babyType: convertBabyTypeForAPI(gender),
      });

      if (!response.IsError) {
        const updatedBaby = [
          {
            id: userInfo.baby[0].id,
            baby_name: name,
            baby_birth: dueDate,
            baby_type: gender,
          },
        ];

        updateStoreAndNavigate(updatedBaby);
        setToastMessage("아기 정보가 수정되었습니다.");
        setIsToastOpen(true);
      } else {
        setToastMessage("오류가 발생했습니다. 다시 시도해주세요.");
        setIsToastOpen(true);
      }
    } catch (error) {
      setToastMessage("오류가 발생했습니다. 다시 시도해주세요.");
      setIsToastOpen(true);
    } finally {
      setIsLoading(false);
    }
  };

  const handleCreateBaby = async () => {
    if (isLoading) return;
    setIsLoading(true);

    try {
      const babyData = {
        babyName: name,
        babyBirth: dueDate,
        babyType: convertBabyTypeForAPI(gender),
      };

      const response = await userHooks.createBabyHooks(babyData);
   
      if (response.status === true) {
        const updatedBaby = [{
          id: response.data.id,
          baby_name: response.data.baby_name,
          baby_birth: response.data.baby_birth,
          baby_type: response.data.baby_type,
        }];
        
        updateStoreAndNavigate(updatedBaby);
        
        setToastMessage("아기 정보가 등록되었습니다.");
        setIsToastOpen(true);
      } else {
        console.log("API 실패:", response);
        setToastMessage("오류가 발생했습니다. 다시 시도해주세요.");
        setIsToastOpen(true);
      }
    } catch (error) {
      setToastMessage("오류가 발생했습니다. 다시 시도해주세요.");
      setIsToastOpen(true);
    } finally {
      setIsLoading(false);
    }
  };

  const updateStoreAndNavigate = (updatedBaby) => {
    updateUserInfo({
      ...userInfo,
      baby: updatedBaby,
    });

    setTimeout(() => {
      window.location.replace("/");
    }, 1000);
  };

  const handleButtonClick = (e) => {
    e.preventDefault();
    if (isEditMode) {
      handleUpdateBaby();
    } else {
      handleCreateBaby();
    }
  };
  return (
    <div
      className="flex flex-col bg-white pt-[3.125rem] md:pt-[4.375rem]"
      style={{ 
        minHeight: "calc(var(--vh, 1vh) * 100)",
        paddingBottom: "max(1.875rem, env(safe-area-inset-bottom))"
      }}
    >
      <BackButton text="아기 정보" />

      <form
        className="flex flex-col gap-[36px] pt-[154px] px-[23px] md:px-[150px]"
        onSubmit={handleButtonClick}
      >
        <input
          type="text"
          placeholder="태명"
          className="border-b h-[31px] md:h-[40px] flex items-center text-[18px] md:text-xl placeholder:text-[#BDBEC6] focus:outline-none"
          value={name}
          onChange={(e) => setName(e.target.value)}
        />
        <div className="relative w-full">
          <div className="relative w-full">
            <input
              type="text"
              placeholder="출산예정일"
              className="w-full border-b bg-transparent h-[31px] md:h-[40px] flex items-center pr-10 text-[18px] md:text-xl placeholder:text-[#BDBEC6] focus:outline-none"
              value={dueDate}
              readOnly
              onClick={() => setIsCalendarOpen(true)}
            />
            <span
              className="absolute right-[2px] top-1/2 -translate-y-1/2 cursor-pointer"
              onClick={() => setIsCalendarOpen(true)}
            >
              <Calendar size={22} />
            </span>
          </div>

          <div className="text-red-500 text-sm mt-2">
            {dateValidation.message}
          </div>
        </div>

        <BottomSheetCalendar
          open={isCalendarOpen}
          onClose={() => setIsCalendarOpen(false)}
          value={dueDate}
          onChange={setDueDate}
        />

        <div className="grid grid-cols-2 gap-x-[4px] gap-y-[10px]">
          {GENDER_OPTIONS.map((option) => (
            <button
              key={option.value}
              type="button"
              className={`min-w-[156px] h-[46px] md:h-[56px] border-[2px] rounded text-[14px] md:text-base font-medium ${
                gender === option.value
                  ? "border-black bg-white text-black"
                  : "border-[#C4C4C4] text-[#C4C4C4]"
              }`}
              onClick={() => setGender(option.value)}
            >
              {option.label}
            </button>
          ))}
        </div>

        <div className="fixed left-0 right-0 bottom-[50px] z-10 px-[23px] md:px-[150px]">
          <div className="flex justify-center max-w-[754px] mx-auto">
            <button
              type="submit"
              className={`w-full h-[54px] rounded-[8px] font-semibold text-[15px] md:text-lg transition ${
                isFormValid && !isLoading
                  ? "bg-[#FEC009] text-white"
                  : "bg-[#E9EAEE] text-[#CCCCCC] cursor-not-allowed"
              }`}
              disabled={isLoading || !isFormValid}
            >
              {isEditMode ? "수정" : "완료"}
            </button>
          </div>
        </div>
      </form>

      <Toast
        isOpen={isToastOpen}
        message={toastMessage}
        onClose={() => setIsToastOpen(false)}
      />
    </div>
  );
};

export default BabyInfo;
